import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { explainPaperStyle, explainExampleTitleStyle, explainPaperTitleStyle, explainPaperTextStyle, explainExamplePaperStyle, explainExampleTextStyle, explainExampleImageBoxStyle} from '../../../../styles/commonStyles';

// 연수 메타데이터
export interface TrainingMetadata {
  id: string;
  title: string;
  category: string;
  description: string;
  instructor: string;
  duration: string;
  date: string;
  tags: string[];
  materials: string[];
  status: 'active' | 'completed' | 'upcoming';
  path: string;
}

export const trainingMetadata: TrainingMetadata = {
  id: 'robot-highschool-iot',
  title: '아두이노 R4 WiFi + Firebase + Cursor AI로 만드는 나만의 IoT 웹앱',
  category: 'ai-edutech',
  description: '아두이노 R4 WiFi 보드의 통신 기능과 구글의 Firebase 실시간 데이터베이스, 그리고 AI 코딩 도구인 Cursor를 활용하여 사물인터넷(IoT) 시스템을 구축하는 과정을 담고 있습니다.',
  instructor: '한지호',
  duration: '3시간',
  date: '2025-12-04',
  tags: ['아두이노', 'Firebase', 'IoT', 'Cursor AI', '웹앱'],
  materials: ['RobotHighSchoolVibeCodingTraining.tsx'],
  status: 'active',
  path: '/teacher-training/AIEdutech/RobotHighSchoolVibeCodingTraining'
};

const RobotHighSchoolVibeCodingTraining: React.FC = () => {
  const navigate = useNavigate();
  const exampleRef = useRef<HTMLDivElement>(null);
  const practiceRef = useRef<HTMLDivElement>(null);
  const [isPdfExpanded, setIsPdfExpanded] = useState(false);

  return (
    <div style={{ 
      width: '100%', 
      minHeight: '100%', 
      boxSizing: 'border-box', 
      padding: '24px', 
      margin: 0, 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'auto'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/teacher-training/AIEdutech')}
          style={{
            marginRight: '16px',
            padding: '8px 16px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ← 돌아가기
        </button>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
          아두이노 R4 WiFi + Firebase + Cursor AI로 만드는 나만의 IoT 웹앱
        </h1>
      </div>
      <div style={{ 
        marginBottom: '24px', 
        border: '1px solid #ccc', 
        padding: '16px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        backgroundColor: 'white'
      }}>
        <h2 style={{ fontWeight: 'bold', marginBottom: '16px', fontSize: '1.25rem' }}>
          📑 목차
        </h2>
        <ul style={{ listStyleType: 'none', padding: 0 }}>
          <li style={{marginBottom: '8px'}}>
            <a href="#기초-이론" style ={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              기초 이론: Firebase란 무엇인가?
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#개발-환경-설정" style ={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              개발 환경 설정: 아두이노 R4 WiFi 및 라이브러리 준비
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#주요-활용-도구" style ={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              주요 활용 도구
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#step-1" style ={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              STEP 1: 아두이노 WiFi 연결 및 LED 제어 (기초)
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#step-2" style ={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              STEP 2: LED 매트릭스 원격 제어 (Web → DB → Arduino)
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#step-3" style ={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              STEP 3: 온습도 센서 데이터 모니터링 (Arduino → DB → Web)
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#step-4" style ={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              STEP 4: 날씨 API 연동 및 스마트 기능 추가
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#final-project" style ={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              Final Project: 나만의 IoT 웹앱 구현하기
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
            <a href="#문제-해결" style ={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
              문제 해결 (Troubleshooting)
            </a>
          </li>
        </ul>
      </div>
      {/* 강의 소개 */}
      <div style={{ display: 'flex', alignItems: 'end', marginBottom: '24px', gap: '16px' }}>
        <p style={{ color: '#666', margin: 0, fontSize: '1rem', lineHeight: '1.6' }}>
          이 강의 자료는 아두이노 R4 WiFi 보드의 통신 기능과 구글의 Firebase 실시간 데이터베이스, 그리고 AI 코딩 도구인 Cursor를 활용하여 사물인터넷(IoT) 시스템을 구축하는 과정을 담고 있습니다.<br/>
        </p>
      </div>

      {/* 주요 활용 도구 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '16px', fontSize: '1.5rem' }} id="주요-활용-도구">
        주요 활용 도구
      </h2>
      <p style={{ color: '#666', marginBottom: '24px', fontSize: '1rem', lineHeight: '1.6' }}>
        본 강의에서 사용하는 핵심 도구는 다음과 같습니다.
      </p>

      {/* 도구 카드 그리드 */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '24px',
        marginBottom: '32px'
      }}>
        {/* Cursor AI IDE 카드 */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <h3 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600', 
            margin: 0,
            color: '#6366F1'
          }}>
            💻 Cursor AI IDE
          </h3>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            fontWeight: '500'
          }}>
            AI 기반 코드 에디터
          </p>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            lineHeight: '1.6'
          }}>
            Cursor는 AI가 코드를 작성하고 개선하는 기능을 내장한 코드 에디터입니다. 
            자연어로 설명하면 AI가 코드를 생성하고, 기존 코드를 분석하여 개선 사항을 제안합니다.
          </p>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>자연어로 코드 작성 요청 및 자동 생성</li>
            <li>기존 코드 분석 및 개선 제안</li>
            <li>버그 찾기 및 수정 제안</li>
            <li>웹앱 개발 시 AI 코딩 보조</li>
          </ul>
          <a 
            href="https://cursor.sh" 
            target="_blank" 
            rel="noopener"
            style={{
              display: 'block',
              width: '100%',
              padding: '12px 24px',
              backgroundColor: '#6366F1',
              color: 'white',
              textAlign: 'center',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'all 0.3s ease',
              marginTop: 'auto'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#4F46E5';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#6366F1';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Cursor 열기 →
          </a>
        </div>

        {/* Arduino IDE 카드 */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <h3 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600', 
            margin: 0,
            color: '#00979D'
          }}>
            🔧 Arduino IDE
          </h3>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            fontWeight: '500'
          }}>
            아두이노 개발 환경
          </p>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            lineHeight: '1.6'
          }}>
            Arduino IDE는 아두이노 보드에 코드를 작성하고 업로드하는 통합 개발 환경입니다. 
            R4 WiFi 보드 매니저를 설치하여 아두이노 프로그래밍을 시작할 수 있습니다.
          </p>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>아두이노 코드 작성 및 컴파일</li>
            <li>시리얼 모니터를 통한 디버깅</li>
            <li>라이브러리 관리 및 설치</li>
            <li>R4 WiFi 보드 지원</li>
          </ul>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '8px',
            marginTop: 'auto'
          }}>
            <a 
              href="https://www.arduino.cc/en/software" 
              target="_blank" 
              rel="noopener"
              style={{
                display: 'block',
                width: '100%',
                padding: '12px 24px',
                backgroundColor: '#00979D',
                color: 'white',
                textAlign: 'center',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#007A7F';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 151, 157, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#00979D';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Arduino IDE 다운로드 →
            </a>
            <a 
              href="https://docs.arduino.cc/hardware/uno-r4-wifi" 
              target="_blank" 
              rel="noopener"
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 24px',
                backgroundColor: '#FFF',
                color: '#00979D',
                textAlign: 'center',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '500',
                fontSize: '0.875rem',
                border: '2px solid #00979D',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#E0F7F8';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 151, 157, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFF';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              R4 WiFi 문서 보기 📚
            </a>
          </div>
        </div>

        {/* Firebase 카드 */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <h3 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600', 
            margin: 0,
            color: '#FFA000'
          }}>
            🔥 Firebase
          </h3>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            fontWeight: '500'
          }}>
            Google의 백엔드 서비스 플랫폼
          </p>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            lineHeight: '1.6'
          }}>
            Firebase는 서버 구축 없이 실시간 데이터베이스, 인증, 호스팅 등을 제공하는 BaaS 플랫폼입니다. 
            아두이노와 웹앱 간의 실시간 데이터 동기화에 사용됩니다.
          </p>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>Realtime Database로 실시간 데이터 동기화</li>
            <li>웹앱과 아두이노 간 데이터 통신</li>
            <li>서버 구축 없이 즉시 사용 가능</li>
            <li>무료 플랜 제공</li>
          </ul>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '8px',
            marginTop: 'auto'
          }}>
            <a 
              href="https://console.firebase.google.com/" 
              target="_blank" 
              rel="noopener"
              style={{
                display: 'block',
                width: '100%',
                padding: '12px 24px',
                backgroundColor: '#FFA000',
                color: 'white',
                textAlign: 'center',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F57C00';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 160, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFA000';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Firebase Console 열기 →
            </a>
            <a 
              href="https://firebase.google.com/docs" 
              target="_blank" 
              rel="noopener"
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 24px',
                backgroundColor: '#FFF',
                color: '#FFA000',
                textAlign: 'center',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '500',
                fontSize: '0.875rem',
                border: '2px solid #FFA000',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#FFF3E0';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 160, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFF';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Firebase 문서 보기 📚
            </a>
          </div>
        </div>

        {/* shadcn/ui 카드 */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <h3 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600', 
            margin: 0,
            color: '#000000'
          }}>
            🎨 shadcn/ui
          </h3>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            fontWeight: '500'
          }}>
            React UI 컴포넌트 라이브러리
          </p>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            lineHeight: '1.6'
          }}>
            shadcn/ui는 Tailwind CSS와 Radix UI를 기반으로 한 오픈소스 React 컴포넌트 라이브러리입니다. 
            복사하여 사용하는 방식으로 프로젝트에 직접 포함되어 완전히 커스터마이징이 가능합니다.
          </p>
          <ul style={{ 
            fontSize: '0.875rem', 
            color: '#666', 
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            <li>아름답고 접근성 높은 UI 컴포넌트 제공</li>
            <li>Tailwind CSS 기반으로 스타일 커스터마이징 용이</li>
            <li>복사하여 사용하는 방식으로 완전한 제어 가능</li>
            <li>웹앱 대시보드 제작에 최적화</li>
          </ul>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '8px',
            marginTop: 'auto'
          }}>
            <a 
              href="https://ui.shadcn.com/" 
              target="_blank" 
              rel="noopener"
              style={{
                display: 'block',
                width: '100%',
                padding: '12px 24px',
                backgroundColor: '#000000',
                color: 'white',
                textAlign: 'center',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#333333';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#000000';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              shadcn/ui 열기 →
            </a>
            <a 
              href="https://ui.shadcn.com/docs" 
              target="_blank" 
              rel="noopener"
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 24px',
                backgroundColor: '#FFF',
                color: '#000000',
                textAlign: 'center',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '500',
                fontSize: '0.875rem',
                border: '2px solid #000000',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F5F5F5';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFF';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              문서 보기 📚
            </a>
          </div>
        </div>
      </div>
      {/* 기초 이론 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem' }} id="기초-이론">
        1. 기초 이론: Firebase & Realtime Database
      </h2>
      
      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        1.1 Firebase란?
      </h3>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        <b>정의:</b> 구글이 제공하는 모바일 및 웹 애플리케이션 개발 플랫폼(BaaS - Backend as a Service)입니다.
      </p>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        <b>IoT에서의 역할:</b> 아두이노(기기)와 웹앱(사용자) 사이의 우체국 역할을 합니다. 서버를 직접 구축할 필요 없이 데이터를 저장하고 전달해 줍니다.
      </p>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        1.2 Realtime Database란?
      </h3>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        <b>특징:</b> NoSQL 클라우드 데이터베이스로, 데이터가 JSON 트리 구조로 저장됩니다.
      </p>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        <b>실시간성:</b> 데이터가 변경되는 즉시 연결된 모든 기기(웹, 앱, 아두이노)에 밀리초(ms) 단위로 동기화됩니다. 즉, 웹에서 버튼을 누르면 아두이노가 즉시 반응합니다.
      </p>

      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px'
      }}>
        <p style={{ color: '#333', margin: 0, fontSize: '1rem', fontWeight: '600', marginBottom: '8px' }}>
          [데이터 흐름도]
        </p>
        <p style={{ color: '#666', margin: 0, fontSize: '0.95rem', lineHeight: '1.6' }}>
          웹앱(사용자) ↔ (읽기/쓰기) ↔ Firebase DB ↔ (읽기/쓰기) ↔ 아두이노(R4)
        </p>
      </div>

      {/* 개발 환경 설정 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem', marginTop: '32px' }} id="개발-환경-설정">
        2. 개발 환경 설정
      </h2>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        2.1 하드웨어 준비
      </h3>
      <ul style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        <li>Arduino UNO R4 WiFi</li>
        <li>DHT11 또는 DHT22 (온습도 센서)</li>
        <li>브레드보드 및 점퍼 케이블</li>
      </ul>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        2.2 소프트웨어 준비
      </h3>
      <ul style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        <li><b>Cursor AI 설치:</b> VS Code 기반의 AI 에디터. (코드 작성 보조용)</li>
        <li><b>Arduino IDE 설치:</b> R4 보드 매니저 설치 필수.</li>
        <li><b>Firebase 프로젝트 생성:</b>
          <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
            <li>Console 접속 → 프로젝트 추가</li>
            <li>Realtime Database 생성 (테스트 모드로 시작)</li>
            <li><b>중요:</b> 설정 &gt; 프로젝트 설정에서 apiKey, databaseURL 등의 Config 정보 복사해두기.</li>
          </ul>
        </li>
      </ul>

      {/* STEP 1 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem', marginTop: '32px' }} id="step-1">
        3. STEP 1: 보드 WiFi 연결 및 LED 제어
      </h2>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        3.1 목표
      </h3>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        아두이노가 인터넷(WiFi)에 접속하고, Firebase와 연결이 되는지 확인합니다.
      </p>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        3.2 Cursor AI 활용 팁
      </h3>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        Cursor의 채팅창(Ctrl+L)에 다음과 같이 질문하여 기본 코드를 얻습니다.
      </p>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        <b>프롬프트 예시:</b>
      </p>
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '16px',
        fontFamily: 'monospace',
        fontSize: '0.95rem',
        lineHeight: '1.8',
        minHeight: '100px'
      }}>
        "아두이노 R4 WiFi 보드를 사용하여 WiFi에 연결하고, 시리얼 모니터에 IP 주소를 출력하는 기본 코드를 작성해줘. WiFi 라이브러리는 'WiFiS3.h'를 사용해야 해."
      </div>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        3.3 핵심 코드 (Arduino)
      </h3>
      <div style={{
        backgroundColor: '#2d2d2d',
        color: '#f8f8f2',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '500px',
        minHeight: '200px',
        fontFamily: 'monospace',
        fontSize: '0.95rem',
        lineHeight: '1.8'
      }}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{`#include "WiFiS3.h"

char ssid[] = "집_와이파이_이름";
char pass[] = "와이파이_비밀번호";

void setup() {
  Serial.begin(9600);
  while (!Serial);

  if (WiFi.status() == WL_NO_MODULE) {
    Serial.println("WiFi 모듈 오류!");
    while (true);
  }

  // WiFi 연결 시도
  while (WiFi.status() != WL_CONNECTED) {
    Serial.print("접속 시도 중... SSID: ");
    Serial.println(ssid);
    WiFi.begin(ssid, pass);
    delay(5000);
  }

  Serial.println("연결 성공!");
  Serial.print("IP 주소: ");
  Serial.println(WiFi.localIP());
}

void loop() {
  // 연결 유지 확인
}`}</pre>
      </div>

      {/* STEP 2 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem', marginTop: '32px' }} id="step-2">
        4. STEP 2: 매트릭스 바꾸기 (Web → Arduino)
      </h2>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        4.1 목표
      </h3>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        웹앱에서 버튼을 누르면 Firebase의 값을 변경하고, 아두이노가 그 값을 읽어 <b>LED 매트릭스 그림(하트, 화살표 등)</b>을 바꿉니다.
      </p>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        4.2 Firebase 구조 설계
      </h3>
      <div style={{ 
        backgroundColor: '#2d2d2d',
        color: '#f8f8f2',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '16px',
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '300px',
        minHeight: '150px',
        fontFamily: 'monospace',
        fontSize: '0.95rem',
        lineHeight: '1.8'
      }}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{`{
  "devices": {
    "r4-001": {
      "mode": 1  // 0: 꺼짐, 1: 하트, 2: 화살표
    }
  }
}`}</pre>
        </div>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        4.3 Cursor AI를 이용한 웹앱 제작
          </h3>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        제공해주신 index.html 파일을 기반으로 설명을 진행합니다.
      </p>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        <b>UI 라이브러리 추천:</b> 웹앱의 UI를 빠르게 구축하려면 <a href="https://ui.shadcn.com/" target="_blank" rel="noopener" style={{ color: '#1976d2', textDecoration: 'none', fontWeight: '600' }}>shadcn/ui</a>를 사용하는 것을 추천합니다. 
        Tailwind CSS 기반의 아름답고 접근성 높은 컴포넌트를 복사하여 사용할 수 있습니다.
      </p>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        <b>프롬프트 예시 (Cursor):</b>
      </p>
          <div style={{ 
        backgroundColor: '#f8f9fa',
        border: '1px solid #e0e0e0',
                borderRadius: '8px',
        padding: '20px',
        marginBottom: '16px',
        fontFamily: 'monospace',
        fontSize: '0.95rem',
        lineHeight: '1.8',
        minHeight: '120px'
      }}>
        "Firebase Web SDK(compat 버전)를 사용하여 HTML 파일을 만들어줘.<br/>
        '하트', '화살표', '끄기' 버튼 3개를 만든다.<br/>
        버튼을 누르면 Realtime Database의 '/devices/r4-001/mode' 경로에 각각 1, 2, 0 숫자를 저장한다.<br/>
        현재 모드 상태를 화면에 텍스트로 보여준다."
      </div>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        4.4 아두이노 구현 (R4 LED Matrix)
      </h3>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        아두이노는 주기적으로(또는 스트림으로) Firebase의 mode 값을 읽어옵니다.
      </p>
      <div style={{
        backgroundColor: '#2d2d2d',
        color: '#f8f8f2',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '300px',
        minHeight: '150px',
        fontFamily: 'monospace',
        fontSize: '0.95rem',
        lineHeight: '1.8'
      }}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{`// (Firebase 라이브러리 설정 필요 - 예: ArduinoFirebase 등)
// 핵심 로직 의사 코드
int mode = firebase.getInt("/devices/r4-001/mode");

if (mode == 1) {
  matrix.loadFrame(HEART_ICON);
} else if (mode == 2) {
  matrix.loadFrame(ARROW_ICON);
} else {
  matrix.clear();
}`}</pre>
      </div>

      {/* STEP 3 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem', marginTop: '32px' }} id="step-3">
        5. STEP 3: 온습도 값 받아오기 (Arduino → Web)
      </h2>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        5.1 목표
      </h3>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        아두이노에 연결된 DHT 센서 값을 읽어 Firebase에 업로드하고, 웹앱에서 실시간으로 그래프나 숫자로 확인합니다.
      </p>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        5.2 데이터 흐름
      </h3>
      <ul style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        <li><b>Arduino:</b> 2초마다 온습도 측정 → Firebase /devices/r4-001/dht 경로에 temp, hum 업데이트.</li>
        <li><b>Web:</b> dbRef.on('value')를 사용하여 데이터가 변할 때마다 화면의 숫자 갱신.</li>
            </ul>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        5.3 웹앱 코드 분석 (제공된 index.html 참고)
      </h3>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        제공된 파일의 이 부분이 핵심입니다.
      </p>
      <div style={{
        backgroundColor: '#2d2d2d',
        color: '#f8f8f2',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '300px',
        minHeight: '150px',
        fontFamily: 'monospace',
        fontSize: '0.95rem',
        lineHeight: '1.8'
      }}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{`// 제공된 파일의 이 부분이 핵심입니다.
const tempRef = database.ref(DHT_PATH + '/temp');
tempRef.on('value', (snapshot) => {
    const temp = snapshot.val();
    temperatureEl.textContent = temp + '°C'; // 화면 업데이트
});`}</pre>
      </div>

      {/* STEP 4 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem', marginTop: '32px' }} id="step-4">
        6. STEP 4: 날씨 API 붙여서 활용하기
      </h2>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        6.1 목표
      </h3>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        웹앱에서 사용자의 위치(또는 서울)의 날씨 정보를 가져와서 보여주고, 이 정보를 Firebase에도 저장하여 아두이노가 날씨에 따라 반응하게 합니다.
      </p>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        6.2 Open-Meteo API 활용
      </h3>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        API 키가 필요 없는 Open-Meteo를 사용합니다.
      </p>
      <p style={{ color: '#666', marginBottom: '8px', fontSize: '1rem', lineHeight: '1.6' }}>
        <b>프롬프트 예시 (Cursor):</b>
      </p>
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '16px',
        fontFamily: 'monospace',
        fontSize: '0.95rem',
        lineHeight: '1.8',
        minHeight: '100px'
      }}>
        "자바스크립트 fetch 함수를 사용하여 Open-Meteo API에서 서울의 현재 날씨, 온도, 날씨 코드를 가져오는 함수를 작성해줘. 가져온 데이터를 콘솔에 출력해."
      </div>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        6.3 시나리오 확장
      </h3>
      <ul style={{ color: '#666', marginBottom: '24px', fontSize: '1rem', lineHeight: '1.6' }}>
        <li><b>Web:</b> 날씨 API 호출 → "비가 옴(Code: 63)" 확인 → Firebase /weather/status에 "rain" 저장.</li>
        <li><b>Arduino:</b> /weather/status가 "rain"이면 LED 매트릭스에 우산 모양 표시.</li>
      </ul>

      {/* Final Project */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem', marginTop: '32px' }} id="final-project">
        7. Final Project: 나만의 IOT 웹앱 구현하기
      </h2>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        7.1 프로젝트 기획 가이드
      </h3>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '1rem', lineHeight: '1.6' }}>
        지금까지 배운 센서(Input), 액추에이터(Output), 통신(Network)을 조합합니다.
      </p>

      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px',
        overflowX: 'auto'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#e9ecef', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #dee2e6' }}>입력 (센서/API)</th>
              <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #dee2e6' }}>처리 (Logic)</th>
              <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #dee2e6' }}>출력 (동작)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>스마트 화분<br/>토양습도센서</td>
              <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>습도가 30% 미만이면</td>
              <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>웹앱에 "물 주세요" 알림, 펌프 작동</td>
            </tr>
            <tr>
              <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>무드등<br/>웹앱 컬러피커</td>
              <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>사용자가 색상 선택</td>
              <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>아두이노 NeoPixel 색상 변경</td>
            </tr>
            <tr>
              <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>날씨 알리미<br/>기상청 API</td>
              <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>내일 비 예보 시</td>
              <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>현관 앞 아두이노가 파란 불 점멸</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.25rem', marginTop: '16px' }}>
        7.2 개발 단계 (Workflow)
      </h3>
      <ol style={{ color: '#666', marginBottom: '24px', fontSize: '1rem', lineHeight: '1.6' }}>
        <li style={{ marginBottom: '8px' }}><b>Cursor와 대화하며 기획:</b> "IoT 스마트 화분을 만들고 싶은데 필요한 부품과 로직을 정리해줘."</li>
        <li style={{ marginBottom: '8px' }}><b>하드웨어 조립:</b> 핀맵 확인 및 연결.</li>
        <li style={{ marginBottom: '8px' }}><b>아두이노 코딩:</b> 센서값 읽기 확인 → Firebase 전송 코드 추가.</li>
        <li style={{ marginBottom: '8px' }}><b>웹 대시보드 제작:</b> <a href="https://ui.shadcn.com/" target="_blank" rel="noopener" style={{ color: '#1976d2', textDecoration: 'none' }}>shadcn/ui</a>를 활용하여 아름다운 UI 컴포넌트로 대시보드 제작.</li>
        <li style={{ marginBottom: '8px' }}><b>통합 테스트:</b> 센서에 입김을 불어보고 웹에서 숫자가 바뀌는지 확인.</li>
      </ol>

      {/* 문제 해결 */}
      <h2 style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.5rem', marginTop: '32px' }} id="문제-해결">
        💡 문제 해결 (Troubleshooting)
      </h2>
      <div style={{
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px'
      }}>
        <p style={{ color: '#856404', margin: 0, fontSize: '1rem', fontWeight: '600', marginBottom: '12px' }}>
          Firebase 연결 실패:
        </p>
        <ul style={{ color: '#856404', margin: 0, paddingLeft: '20px', fontSize: '0.95rem', lineHeight: '1.6' }}>
          <li>WiFi 비밀번호 오타 확인 (2.4GHz만 지원하는 경우가 많음).</li>
          <li>Firebase 규칙(Rules)이 read: true, write: true로 설정되어 있는지 확인 (테스트용).</li>
        </ul>
      </div>
      <div style={{
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px'
      }}>
        <p style={{ color: '#856404', margin: 0, fontSize: '1rem', fontWeight: '600', marginBottom: '12px' }}>
          센서 값 0/NaN:
        </p>
        <ul style={{ color: '#856404', margin: 0, paddingLeft: '20px', fontSize: '0.95rem', lineHeight: '1.6' }}>
          <li>점퍼 케이블 연결 상태 확인.</li>
          <li>센서 라이브러리(DHT sensor library) 설치 여부 확인.</li>
        </ul>
      </div>
      <div style={{
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
            borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px'
      }}>
        <p style={{ color: '#856404', margin: 0, fontSize: '1rem', fontWeight: '600', marginBottom: '12px' }}>
          웹앱이 동작 안 함:
        </p>
        <ul style={{ color: '#856404', margin: 0, paddingLeft: '20px', fontSize: '0.95rem', lineHeight: '1.6' }}>
          <li>브라우저 개발자 도구(F12) &gt; Console 탭의 에러 메시지 확인.</li>
        </ul>
      </div>
    </div>
  );
};

export default RobotHighSchoolVibeCodingTraining;